import { useState, useEffect, useRef, useCallback } from 'react';
import { roundPrice } from '@hero/core';

// Format number with commas (e.g., 50000 -> "50,000")
const formatNumberWithCommas = (value: string): string => {
  // Remove commas and get clean numeric value
  const cleanValue = value.replace(/,/g, '');

  // Split by decimal point
  const parts = cleanValue.split('.');
  const integerPart = parts[0];
  const decimalPart = parts[1];

  // Add commas to integer part
  const formattedInteger = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, ',');

  // Combine with decimal part if exists
  return decimalPart !== undefined ? `${formattedInteger}.${decimalPart}` : formattedInteger;
};

interface UsePriceEditOptions {
  value: number | undefined;
  onChange: (value: number | undefined) => void;
  onCursorPositionChange?: (position: number) => void;
}

interface UsePriceEditReturn {
  inputValue: string;
  handleChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleBlur: () => void;
  setCursorPosition: (position: number) => void;
  isUserTyping: boolean;
}

/**
 * Hook for managing price input with formatting, cursor position, and rounding
 *
 * @param options - Configuration options
 * @param options.value - Current numeric value
 * @param options.onChange - Callback when value changes
 * @param options.onCursorPositionChange - Optional callback for cursor position changes
 * @returns Object with input handlers and state
 */
export const usePriceEdit = ({
  value,
  onChange,
  onCursorPositionChange,
}: UsePriceEditOptions): UsePriceEditReturn => {
  const [inputValue, setInputValue] = useState<string>('');
  const cursorPositionRef = useRef<number | null>(null);
  const isUserTypingRef = useRef<boolean>(false);
  const lastClearedRef = useRef<boolean>(false);

  // Sync inputValue with external value (only when user is not typing)
  useEffect(() => {
    if (!isUserTypingRef.current && !lastClearedRef.current) {
      if (value === undefined || value === 0) {
        // Only set to empty if input is not already empty (avoid unnecessary updates)
        if (inputValue !== '') {
          setInputValue('');
        }
      } else {
        const formatted = formatNumberWithCommas(value.toString());
        // Only update if the formatted value is different from current input
        // This prevents restoring old values when user clears the input
        if (formatted !== inputValue) {
          setInputValue(formatted);
        }
      }
    }
    // Reset the cleared flag after sync
    if (lastClearedRef.current && (value === undefined || value === 0)) {
      lastClearedRef.current = false;
    }
  }, [value, inputValue]);

  // Calculate new cursor position after formatting
  const calculateNewCursorPosition = useCallback(
    (oldValue: string, newValue: string, oldCursorPos: number): number => {
      // If values are the same, return the same position
      if (oldValue === newValue) {
        return oldCursorPos;
      }

      // Get the text before cursor in old value (excluding commas and decimal point)
      const beforeCursor = oldValue.substring(0, oldCursorPos);
      const digitsBeforeCursor = beforeCursor.replace(/[.,]/g, '').length;

      // If we're at the end of the old value, keep cursor at the end of new value
      if (oldCursorPos >= oldValue.length) {
        return newValue.length;
      }

      // Find the position in new value that has the same number of digits before it
      let digitsCounted = 0;
      let newPosition = newValue.length; // Default to end

      for (let i = 0; i < newValue.length; i++) {
        const char = newValue[i];
        if (char !== ',' && char !== '.') {
          digitsCounted++;
          if (digitsCounted === digitsBeforeCursor) {
            // Position cursor after this digit
            newPosition = i + 1;
            break;
          }
        }
      }

      return newPosition;
    },
    [],
  );

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      isUserTypingRef.current = true;
      const input = e.target;
      const newValue = e.target.value;
      const oldValue = inputValue;
      const oldCursorPos = input.selectionStart || 0;

      // Remove any non-numeric characters except decimal point and commas
      let numericValue = newValue.replace(/[^0-9.,]/g, '');

      // Prevent multiple decimal points - keep only the first one
      const decimalIndex = numericValue.indexOf('.');
      if (decimalIndex !== -1) {
        const beforeDecimal = numericValue.substring(0, decimalIndex);
        const afterDecimal = numericValue.substring(decimalIndex + 1).replace(/\./g, '');
        numericValue = beforeDecimal + '.' + afterDecimal;
      }

      // Allow empty string
      if (numericValue === '') {
        setInputValue('');
        onChange(undefined);
        cursorPositionRef.current = 0;
        if (onCursorPositionChange) {
          onCursorPositionChange(0);
        }
        // Mark that we've cleared the input to prevent sync effect from restoring old value
        lastClearedRef.current = true;
        // Keep typing flag true for a bit to prevent sync effect from restoring old value
        setTimeout(() => {
          isUserTypingRef.current = false;
        }, 150);
        return;
      }

      // Remove commas for processing, but preserve decimal structure
      const cleanNumericValue = numericValue.replace(/,/g, '');

      // Check if the value ends with a decimal point (user is typing decimals)
      const endsWithDecimal = cleanNumericValue.endsWith('.');
      const hasDecimalPoint = cleanNumericValue.includes('.');

      // Detect if user is deleting (new value is shorter than old value)
      const isDeleting = newValue.length < oldValue.length;

      // Limit decimal places to 2 digits
      let processedValue = cleanNumericValue;
      if (hasDecimalPoint) {
        const decimalParts = processedValue.split('.');
        if (decimalParts.length === 2) {
          // Limit decimal part to 2 digits
          const decimalPart = decimalParts[1].substring(0, 2);
          processedValue = decimalParts[0] + '.' + decimalPart;
        }
      }

      // If deleting and the result is just a decimal point with no digits after, remove the decimal point
      if (isDeleting && endsWithDecimal && processedValue.replace(/\./g, '').length > 0) {
        // Check if there are digits after the decimal point
        const decimalParts = processedValue.split('.');
        if (decimalParts.length === 2 && decimalParts[1] === '') {
          // User deleted the last digit after decimal point, remove the decimal point
          processedValue = decimalParts[0] || '0';
        }
      }

      let formattedValue: string;
      let valueToSet: number | undefined;

      // If user is typing a decimal (ends with .), preserve the decimal point in display
      if (processedValue.endsWith('.') && !isDeleting) {
        // Extract integer part and format it, then add the decimal point
        const integerPart = processedValue.slice(0, -1) || '0';
        const integerNum = parseFloat(integerPart) || 0;
        const formattedInteger = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
        formattedValue = `${formattedInteger}.`;
        valueToSet = integerNum;
      } else {
        // Parse the full number
        const numberValue = parseFloat(processedValue);

        if (!isNaN(numberValue)) {
          // Format preserving decimal places
          formattedValue = formatNumberWithCommas(numberValue.toString());
          valueToSet = numberValue;
        } else {
          // If not a valid number but has structure, try to preserve it
          if (processedValue.includes('.')) {
            // Format what we have
            formattedValue = formatNumberWithCommas(processedValue);
            const partialValue = parseFloat(processedValue);
            valueToSet = isNaN(partialValue) ? undefined : partialValue;
          } else {
            // Invalid input, just update display
            formattedValue = formatNumberWithCommas(processedValue);
            valueToSet = undefined;
          }
        }
      }

      setInputValue(formattedValue);
      // Don't round on every keystroke - allow typing decimals
      onChange(valueToSet);

      // Calculate and store new cursor position
      const newCursorPos = calculateNewCursorPosition(oldValue, formattedValue, oldCursorPos);
      cursorPositionRef.current = newCursorPos;
      if (onCursorPositionChange) {
        onCursorPositionChange(newCursorPos);
      }

      // Reset typing flag after a short delay to allow external updates
      setTimeout(() => {
        isUserTypingRef.current = false;
      }, 100);
    },
    [inputValue, onChange, onCursorPositionChange, calculateNewCursorPosition],
  );

  const handleBlur = useCallback(() => {
    // Apply rounding when user finishes typing
    if (value !== undefined && value !== 0) {
      const roundedValue = roundPrice(value);
      onChange(roundedValue);
    }
  }, [value, onChange]);

  const setCursorPosition = useCallback(
    (position: number) => {
      cursorPositionRef.current = position;
      if (onCursorPositionChange) {
        onCursorPositionChange(position);
      }
    },
    [onCursorPositionChange],
  );

  return {
    inputValue,
    handleChange,
    handleBlur,
    setCursorPosition,
    isUserTyping: isUserTypingRef.current,
  };
};
